import { useMemo } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Loader2 } from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { useGetChecks, useUpdateFeatureCheckConfig } from "@/resources/validator-registry";
import type { AiPrompt } from "@/types/ai-prompt";
import type { RegistryCheck, RegistryFeatureConfig } from "@/types/validator-registry";
import { EditableText } from "./EditableText";
import { SEVERITY_LABELS } from "./constants";

type PromptValidatorChecksProps = {
  prompt: AiPrompt;
  onNavigateToValidator: () => void;
};

type ScopedCheck = {
  check: RegistryCheck;
  config: RegistryFeatureConfig;
};

/**
 * Validator checks scoped to a single generation prompt: the shared parent
 * rule (read-only, edited from the consolidated Validator page) plus the
 * prompt-specific refinement, editable here. Scoping is an EXACT match on
 * `feature_check_config.feature_name === prompt.feature_key` -- unlike the
 * old feature-level panel this replaces, which joined by a coarse
 * `promptKeyPrefix` prefix across every prompt in the feature.
 */
export function PromptValidatorChecks({ prompt, onNavigateToValidator }: PromptValidatorChecksProps) {
  const { data: checks, isLoading, isError } = useGetChecks();

  const scoped = useMemo<ScopedCheck[]>(() => {
    if (!checks) return [];
    // A check runs on this prompt only when BOTH the check is enabled AND the
    // scope (feature_check_config) is active -- the runtime requires both, so
    // inert/staged scopes (active=false) must not read as live checks here.
    return checks
      .filter((check) => check.active)
      .flatMap((check) => {
        const config = check.feature_configs.find(
          (fc) => fc.feature_name === prompt.feature_key && fc.active
        );
        return config ? [{ check, config }] : [];
      });
  }, [checks, prompt.feature_key]);

  return (
    <div className="space-y-3 min-w-0" data-testid="prompt-validator-checks">
      <div className="flex items-center justify-between gap-3">
        <h3 className="text-base font-semibold text-foreground">
          Validator Checks
        </h3>
        <Button
          variant="link"
          size="sm"
          className="h-auto p-0 text-sm"
          onClick={onNavigateToValidator}
        >
          Manage in Validator
        </Button>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-8" data-testid="prompt-validator-checks-loading">
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        </div>
      ) : isError ? (
        <div
          className="p-4 rounded-lg border border-dashed border-destructive/40 text-center"
          data-testid="prompt-validator-checks-error"
        >
          <p className="text-sm text-muted-foreground">
            Couldn&apos;t load validator checks. Try refreshing the page.
          </p>
        </div>
      ) : scoped.length === 0 ? (
        <div
          className="p-4 rounded-lg border border-dashed border-border text-center"
          data-testid="prompt-validator-checks-empty"
        >
          <p className="text-sm text-muted-foreground">
            No validator checks are applied to this prompt.
          </p>
        </div>
      ) : (
        <Accordion type="multiple" className="w-full">
          {scoped.map(({ check, config }) => (
            <AccordionItem key={check.id} value={check.id}>
              <AccordionTrigger data-testid={`prompt-validator-check-${check.id}`}>
                <span className="flex flex-1 items-center justify-between gap-3 pr-2">
                  <span className="flex items-center gap-2 text-left">
                    <span className="text-sm font-semibold text-foreground">
                      {check.label}
                    </span>
                    {check.app_side && (
                      <Badge
                        variant="secondary"
                        className="text-xs"
                        data-testid={`prompt-validator-check-app-side-${check.id}`}
                      >
                        Application-side
                      </Badge>
                    )}
                  </span>
                  <Badge
                    variant={config.severity === "hard_fail" ? "error" : "warning"}
                    className="text-xs"
                    data-testid={`prompt-validator-check-severity-${check.id}`}
                  >
                    {SEVERITY_LABELS[config.severity]}
                  </Badge>
                </span>
              </AccordionTrigger>
              <AccordionContent>
                <PromptCheckDetail check={check} config={config} />
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      )}
    </div>
  );
}

function PromptCheckDetail({ check, config }: ScopedCheck) {
  const updateConfig = useUpdateFeatureCheckConfig(config.feature_name, check.id);

  return (
    <div className="ml-2 border-l-2 border-primary pl-4 space-y-4">
      <EditableText
        readOnly
        size="sm"
        value={check.base_rule}
        label={
          <span className="text-sm">
            <span className="font-semibold text-foreground">Parent rule</span>{" "}
            <span className="font-medium text-muted-foreground">
              — shared across every prompt; edit in the Validator
            </span>
          </span>
        }
        testId={`prompt-validator-check-base-rule-${check.id}`}
      />

      {check.app_side ? (
        <p
          className="text-sm text-muted-foreground"
          data-testid={`prompt-validator-check-app-side-note-${check.id}`}
        >
          This check runs deterministically in the app, not through the prompt, so it takes no
          refinement.
        </p>
      ) : (
        <EditableText
          size="sm"
          rows={4}
          value={config.refinement}
          emptyText="Nothing added yet — the base rule alone applies."
          label={
            <span className="text-sm">
              <span className="font-semibold text-foreground">Refinement</span>{" "}
              <span className="font-medium text-muted-foreground">— this prompt only, optional</span>
            </span>
          }
          onSave={(v) => {
            const trimmed = v.trim();
            return updateConfig.mutateAsync({ refinement: trimmed === "" ? null : v });
          }}
          savedMessage={`${check.label} refinement saved`}
          ariaLabel={`${check.label} refinement`}
          testId={`prompt-validator-check-refinement-${check.id}`}
        />
      )}
    </div>
  );
}
